import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
  Divider,
  Stack,
} from '@landler/component-library';
import { FC, Key, ReactNode } from 'react';

import { useAccordionState } from '@/hooks/useAccordionState';

export interface SidebarItem {
  content: ReactNode;
  collapsedContent?: ReactNode;
  style?: string;
  key?: Key;
  children?: SidebarItem[];
  isActive?: boolean;
  isCollapsed?: boolean;
}

export interface SidebarProps {
  header?: SidebarItem;
  footer?: SidebarItem;
  items?: SidebarItem[];
  className?: string;
  noContentText?: string;
  isCollapsed?: boolean;
}

export const Sidebar: FC<SidebarProps> = ({
  header,
  footer,
  items,
  className,
  noContentText,

  isCollapsed = false,
}) => {
  const spacingClasses = 'justify-start px-0 py-3';

  return (
    <Stack
      data-testid='sidebar-container'
      className={cn('top-0 z-appbar flex flex-col right-0 left-auto', className, !isCollapsed && 'h-full')}
    >
      {header?.content && (
        <Stack data-testid='sidebar-header' className={cn(spacingClasses, header?.style)}>
          {header.content}
        </Stack>
      )}
      {!isCollapsed && items && items.length > 0 && (
        <Stack
          data-testid='sidebar-items-list'
          spacing={2}
          className={cn(spacingClasses, 'p-6 flex-1 overflow-y-auto')}
        >
          {items.map((item, index) => {
            const itemKey = String(item.key) || String(index);
            const hasChildren = item.children && item.children.length > 0;
            if (hasChildren) {
              return (
                <AccordionItemWrapper
                  key={`${itemKey}-${index}`}
                  itemKey={itemKey}
                  item={item}
                  isCollapsed={false}
                  isActive={item.isActive}
                />
              );
            }

            return (
              <div
                key={`${itemKey}-${index}`}
                className={cn('w-full', isCollapsed ? 'justify-center' : '', item?.style)}
              >
                {isCollapsed ? item.collapsedContent : item.content}
              </div>
            );
          })}
        </Stack>
      )}

      {!isCollapsed && items && items.length === 0 && noContentText && (
        <Stack data-testid='sidebar-no-content-text' className={spacingClasses}>
          <span className='px-4'>{noContentText}</span>
        </Stack>
      )}

      {!isCollapsed && footer?.content && (
        <Stack data-testid='sidebar-footer' className={cn(spacingClasses, footer?.style)}>
          <Divider />
          {isCollapsed ? footer.collapsedContent : footer.content}
        </Stack>
      )}
    </Stack>
  );
};

interface AccordionItemWrapperProps {
  itemKey: string;
  item: SidebarItem;
  isCollapsed: boolean;
  isActive?: boolean;
}

const AccordionItemWrapper: FC<AccordionItemWrapperProps> = ({ itemKey, item, isCollapsed, isActive }) => {
  // Generate accordion ID based on the item key
  const accordionId = `sidebar-accordion-${itemKey}`;

  const { openValue, handleValueChange } = useAccordionState({
    id: accordionId,
    persist: true,
  });

  return (
    <Accordion type='single' collapsible value={openValue} onValueChange={handleValueChange}>
      <AccordionItem value={itemKey} className='rounded-md'>
        <AccordionTrigger
          data-testid={`sidebar-accordion-trigger-${itemKey}`}
          className={cn('hover:bg-neutral-hover p-0 pr-4 rounded-md', isActive && 'bg-neutral-hover')}
        >
          {isCollapsed ? item.collapsedContent : item.content}
        </AccordionTrigger>
        <AccordionContent className='py-1 pl-1.5'>
          <Stack spacing={1} className='pl-4'>
            {item.children?.map((child, childIdx) => (
              <div
                key={`${child?.key}-${childIdx}`}
                className={cn('w-full', isCollapsed ? 'justify-center' : '', child?.style)}
              >
                {isCollapsed ? child.collapsedContent : child.content}
              </div>
            ))}
          </Stack>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
